// Plain-text export for copy/paste into e-mail or chat. Kept deliberately simple (no Markdown,
// ASCII list markers) so it survives every mail client unchanged.
import type { DocumentFull } from '../../../shared/types';
import { formatDue, formatOffset } from '../lib/format';
import {
  clipBody,
  inspectionEntries,
  inspectionHeaderRows,
  meetingDateLine,
  noteText,
  partHeading,
  participantNames,
  placeholderFor,
  sortedClips,
  textParagraphs,
  type ClipBody,
} from './common';

function minutesLines(doc: DocumentFull): string[] {
  const minutes = doc.minutes;
  if (!minutes) return [];
  const lines: string[] = [];
  const summary = textParagraphs(minutes.povzetek);
  if (summary.length > 0) lines.push('', 'Povzetek', ...summary);
  const topics = minutes.teme.map((t) => t.trim()).filter(Boolean);
  if (topics.length > 0) lines.push('', 'Teme', ...topics.map((t) => `- ${t}`));
  const decisions = minutes.sklepi.map((s) => s.trim()).filter(Boolean);
  if (decisions.length > 0) lines.push('', 'Sklepi', ...decisions.map((s, i) => `${i + 1}. ${s}`));
  const tasks = minutes.naloge.filter((n) => n.opis.trim());
  if (tasks.length > 0) {
    lines.push(
      '',
      'Naloge',
      ...tasks.map((n) => `- ${n.opis.trim()} (nosilec: ${n.nosilec?.trim() || '—'}; rok: ${n.rok?.trim() || '—'})`),
    );
  }
  return lines;
}

function meetingBodyLines(body: ClipBody): string[] {
  const note = 'note' in body && body.note ? [body.note] : [];
  switch (body.kind) {
    case 'turns':
      return [...note, ...body.turns.map((t) => `[${formatOffset(t.startMs)}] ${t.name ? `${t.name}: ` : ''}${t.text}`)];
    case 'paragraphs':
      return [...note, ...body.paragraphs];
    default:
      return [placeholderFor(body) ?? ''];
  }
}

function meetingToText(doc: DocumentFull): string {
  const lines: string[] = [doc.title, meetingDateLine(doc)];
  const names = participantNames(doc);
  if (names.length > 0) lines.push(`Udeleženci: ${names.join(', ')}`);
  lines.push(...minutesLines(doc));

  const clips = sortedClips(doc);
  lines.push('', 'Prepis');
  if (clips.length === 0) lines.push('Ni posnetkov.');
  clips.forEach((clip, index) => {
    if (clips.length > 1) lines.push('', partHeading(index, clip));
    lines.push(...meetingBodyLines(clipBody(doc, clip, { turns: true })));
  });
  return lines.join('\n');
}

function inspectionToText(doc: DocumentFull): string {
  const lines: string[] = [doc.title];
  for (const row of inspectionHeaderRows(doc)) lines.push(`${row.label}: ${row.lines.join(', ')}`);

  lines.push('', 'Ugotovitve');
  const entries = inspectionEntries(doc);
  if (entries.length === 0) lines.push('Ni vnosov.');
  for (const entry of entries) {
    const prefix = entry.time ? `${entry.number}. (${entry.time}) ` : `${entry.number}. `;
    const paragraphs = entry.body.kind === 'paragraphs' ? [...entry.body.paragraphs] : [placeholderFor(entry.body) ?? ''];
    // A status note (re-transcription in progress) follows the entry's last line.
    if (entry.body.kind === 'paragraphs' && entry.body.note) paragraphs.push(`${paragraphs.pop() ?? ''} ${entry.body.note}`.trim());
    const [first = '', ...rest] = paragraphs;
    lines.push(prefix + first);
    // Continuation lines are indented so the numbering stays readable in a monospace-less mail client too.
    const indent = ' '.repeat(`${entry.number}. `.length);
    for (const line of rest) lines.push(indent + line);
  }

  const notes = textParagraphs(doc.meta.opombe);
  if (notes.length > 0) lines.push('', 'Opombe', ...notes);
  return lines.join('\n');
}

function noteToText(doc: DocumentFull): string {
  const text = noteText(doc);
  return doc.dueAt ? `${text}\nRok: ${formatDue(doc.dueAt)}` : text;
}

/** Plain text suitable for copy/paste into e-mail (meeting: "[00:12] Janez: ..."; inspection: numbered list; note: text + due). */
export function documentToText(doc: DocumentFull): string {
  switch (doc.type) {
    case 'meeting':
      return meetingToText(doc);
    case 'inspection':
      return inspectionToText(doc);
    case 'note':
    default:
      return noteToText(doc);
  }
}
